import { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import EnquiryForm from "@/components/EnquiryForm";
import { DETAILS } from "@/components/Contact";
import { useBrands } from "@/components/Yachts";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "ref"];

export default function EnquiryPage() {
  const [params] = useSearchParams();
  const { data: brands = [] } = useBrands();

  useEffect(() => {
    document.title = "Enquire | Modi Marine";
    window.scrollTo(0, 0);
  }, []);

  const presetInterest = useMemo(() => {
    const raw = (params.get("interest") || params.get("brand") || "").toLowerCase();
    if (!raw) return "";
    const brand = brands.find((b) => b.slug === raw || b.name.toLowerCase() === raw);
    return brand ? brand.name : params.get("interest") || "";
  }, [params, brands]);

  const source = useMemo(() => {
    const parts = UTM_KEYS.filter((k) => params.get(k)).map((k) => `${k.replace("utm_", "")}=${params.get(k)}`);
    return (parts.length ? parts.join(" · ") : "Enquiry page").slice(0, 300);
  }, [params]);

  return (
    <section className="bg-navy px-6 pb-20 pt-32 lg:px-12 lg:pb-28 lg:pt-40" data-testid="enquiry-page">
      <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-5">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mb-6 text-[10px] uppercase tracking-[0.45em] text-gold sm:text-xs" data-testid="enquiry-page-overline">
            Private Enquiry
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} className="font-serif text-4xl font-light leading-[1.02] tracking-tight text-navy-ink sm:text-5xl lg:text-6xl" data-testid="enquiry-page-heading">
            Begin the
            <span className="italic text-gold-light"> conversation.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9, delay: 0.3 }} className="mt-6 max-w-md text-sm font-light leading-relaxed text-mist sm:text-base" data-testid="enquiry-page-description">
            Tell us a little about yourself and the yacht you have in mind. A member of our private office will respond personally within one working day — by phone, email or in person, wherever you are on the Indian coastline.
          </motion.p>

          <div className="mt-12 space-y-7">
            {DETAILS.map((d) => (
              <div key={d.label} className="flex items-start gap-5" data-testid={`enquiry-page-${d.testId}`}>
                <d.icon size={20} strokeWidth={1} className="mt-1 text-gold" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-mist">{d.label}</p>
                  <p className="mt-2 font-sans text-base text-navy-ink sm:text-lg">{d.value}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-12 max-w-md text-xs font-light leading-relaxed text-mist/80" data-testid="enquiry-page-privacy">
            Your details are shared only with Modi Marine. We never ask for passwords or card details by email.
          </p>
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.2 }} className="border border-navy-ink/10 bg-navy-light/40 p-8 lg:col-span-7 lg:p-12" data-testid="enquiry-page-form-panel">
          <EnquiryForm prefix="enquiry-page" presetInterest={presetInterest} source={source} submitLabel="Send Enquiry" />
        </motion.div>
      </div>
    </section>
  );
}
